/**
 * widgetPresets — a card arrives WORKING, not blank (2026-08-17).
 *
 * ## The problem this exists to solve
 *
 * Adding a card used to mint an empty definition: no name, no data source, no
 * measure. The card then drew a placeholder telling you three things were missing,
 * and the panel opened on a form of seven collapsed sections, five of them locked
 * until you guessed that "Origen de datos" had to come first. Reported, exactly:
 * *"i dont know how to even make an alert"* — and that is the honest outcome, not a
 * user error. Nothing on screen said what an alert needed, in what order, or what a
 * finished one looked like.
 *
 * A palette entry is a statement of INTENT ("I want an alert here"). The editor
 * knows what a working alert is; making the user reconstruct it field by field, from
 * an empty form, is the wrong division of labour. So the palette now hands back a
 * complete, schema-valid, immediately-rendering card, and configuring it becomes
 * *adjusting something that already works* rather than *assembling it from nothing*.
 *
 * That difference also removes a whole class of errors by construction: a preset
 * card is never in the "incomplete" state, so it never shows a missing-fields notice
 * and never fails the save gate. **The way to stop showing people errors is to stop
 * putting them in states that produce errors.**
 *
 * ## Analytics presets are resolved against the LIVE catalog, never hard-coded
 *
 * A literal `cube: 'Sales', measures: ['Sales.netRevenue']` would be a guess about
 * another team's schema, and a wrong guess produces the worst outcome available: a
 * card that looks configured and renders "Member not found". So the analytics
 * presets take the cube catalog that the editor already fetches for the builder and
 * pick the first cube that actually HAS a measure. If the catalog has not arrived
 * yet, the card falls back to the blank definition — the old behaviour, which is
 * still correct, just less helpful.
 *
 * ## Readmodel and static presets ARE literal, and that is fine
 *
 * `READMODEL_CATALOG` is front-end data in this repo, so naming a collection here is
 * a reference to a sibling module rather than a guess about a remote schema. The
 * collections chosen are the ones a seller recognises; `getReadmodelCollection`
 * still guards them, so a renamed collection degrades to the error tile with a named
 * cause instead of an empty card.
 */

import { uuidv7 } from 'uuidv7';
import type { NormalizedCubeCatalog } from './Widgets/WidgetBuilder/cubeMeta';
import type { WidgetDefinition } from './Widgets/WidgetBuilder/types';
import type { WidgetType } from './Widgets/types';
import { widgetLabelFallback } from './widgetCatalog';

/** The empty shell every preset is built on top of. */
const blankDefinition = ( type: WidgetType, id: string ): WidgetDefinition => ({
  id,
  name: '',
  cube: '',
  measures: [],
  dimensions: [],
  filters: [],
  orderBy: [],
  dateRange: null,
  type,
  widgetComponentProps: {}
});

/**
 * The readmodel-backed presets — the four cards that had no discoverable path at
 * all, since their whole configuration is a collection name the user cannot guess.
 *
 * Each is a real, useful card on arrival. `alert` deliberately starts at `medium`
 * severity: `low` reads as "ignore me" and `high` asserts an urgency nobody has
 * chosen yet, so the middle claim is the only honest default for a card the editor
 * invented rather than the user.
 */
/*
 * ⚠️ NO SEED CAPTION (2026-08-19). Both of these used to ship one — `count` said
 * "Publicaciones totales" and `alert` said "Preguntas recibidas" — and it was
 * reported as a bug twice over: *"on count widget the subtext below the number
 * always says publicaciones regardless what it is"*, *"alert widget always have the
 * same subtext too"*.
 *
 * They were not a bug in the renderer. They were these two literals, and what made
 * them look like one is that the caption had NO editor — the inspector offered
 * severity, href, filters and limit, but never the caption. So a preset's guess
 * about the subject became a permanent, unremovable label the moment the author
 * re-pointed the card at a different collection.
 *
 * Both halves are fixed: the caption is editable now (`ReadmodelSourceSection`), and
 * a preset no longer asserts one. A seed value is only honest when it is either
 * true for every card of that type or reachable to change — a caption naming ONE
 * collection is neither.
 *
 * The `name`s stay: they are the card TITLE, they are visibly editable at the top
 * of the inspector, and a card with no name at all is worse than one with a
 * starting point.
 */
const READMODEL_PRESETS: Partial<Record<WidgetType, Partial<WidgetDefinition>>> = {
  count: {
    name: 'Publicaciones',
    collection: 'items_publishing_publications_readmodel',
    widgetComponentProps: {}
  },
  alert: {
    name: 'Preguntas de compradores',
    collection: 'crm_questions_readmodel',
    // `href` makes the tile a call to action rather than a number to stare at —
    // the point of an alert is that you can go and resolve it. Kept (unlike the
    // caption) because it is editable, and because a dead-end alert is a worse
    // starting point than one that goes somewhere plausible.
    widgetComponentProps: { severity: 'medium', href: '/interactions' }
  },
  list: {
    name: 'Últimas órdenes',
    collection: 'sales_orders_readmodel',
    limit: 5
  },
  progress: {
    name: 'Procesos por lote',
    collection: 'batch_processes_readmodel',
    limit: 5
  }
};

/** The content-only presets. They need nothing but a name and something to show. */
const STATIC_PRESETS: Partial<Record<WidgetType, Partial<WidgetDefinition>>> = {
  text: {
    name: 'Nota',
    widgetComponentProps: { content: '## Título\n\nEscribí acá tus notas sobre el informe.' }
  },
  heading: {
    name: 'Sección',
    title: 'Nueva sección'
  },
  spacer: {
    name: 'Espacio'
  }
};

/**
 * The first cube that has at least one measure, with that measure.
 *
 * "Has at least one measure" rather than simply "the first cube": a dimension-only
 * cube would produce a card that is schema-valid and permanently empty, which is
 * exactly the silent-wrong-answer this module exists to avoid.
 */
const firstUsableMeasure = (
  catalog: NormalizedCubeCatalog | null
): { cube: string; measure: string } | null => {
  const cube = catalog?.cubes.find(( c ) => c.measures.length > 0 );
  if ( !cube ) return null;
  return { cube: cube.name, measure: cube.measures[0].value };
};

/**
 * A ready-to-render definition for `type`.
 *
 * Always returns something. When the analytics catalog is unavailable the result is
 * the blank definition — the card is then "incomplete" and says so, which is the
 * correct behaviour for a card the editor genuinely could not finish.
 */
export const presetDefinitionFor = (
  type: WidgetType,
  catalog: NormalizedCubeCatalog | null,
  id: string = uuidv7()
): WidgetDefinition => {
  const base = blankDefinition( type, id );

  const literal = READMODEL_PRESETS[type] ?? STATIC_PRESETS[type];
  if ( literal ) return { ...base, ...literal };

  // Analytics. `name` falls back to the catalog's human label so the card is never
  // nameless — `name` is schema-required, and "Barras" is a better starting point
  // than an empty field the user has to notice is empty.
  const picked = firstUsableMeasure( catalog );
  if ( !picked ) return base;

  return {
    ...base,
    name: widgetLabelFallback( type ),
    cube: picked.cube,
    measures: [picked.measure]
  };
};
